/**
 * Return to Yard Modal
 *
 * Modal dialog for returning containers to storage yards.
 * Uses react-hook-form with Zod validation for robust form handling.
 */

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useReturnToYard } from '@/hooks/useContainers';
import { useStorageYards } from '@/hooks/useStorageYards';
import { returnToYardSchema, type ReturnToYardDTO } from '@/schemas/containerSchema';
import { containerConditionEnum } from '@/schemas/containerSchema';
import { ApiError } from '@/services/api';
import type { Container } from '@/services/resource-container.service';

interface ReturnToYardModalProps {
  open: boolean;
  onClose: () => void;
  container: Container | null;
}

// Extended form data to include condition update and notes
interface ReturnToYardFormData extends ReturnToYardDTO {
  condition?: string;
  notes?: string;
}

export function ReturnToYardModal({ open, onClose, container }: ReturnToYardModalProps) {
  const returnToYard = useReturnToYard();
  const { data: yardData } = useStorageYards();

  const yards = yardData?.yards || [];
  const activeYards = yards.filter(yard => yard.isActive);

  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<ReturnToYardFormData>({
    resolver: zodResolver(returnToYardSchema),
    defaultValues: {
      yardAddress: '',
      returnDate: new Date().toISOString().slice(0, 16), // Default to now in datetime-local format
      condition: container?.condition,
      notes: '',
    },
  });

  const onSubmit = async (data: ReturnToYardFormData) => {
    if (!container) return;

    try {
      // Convert datetime-local to ISO string
      const returnDate = new Date(data.returnDate).toISOString();

      await returnToYard.mutateAsync({
        id: container.id,
        data: {
          yardAddress: data.yardAddress,
          returnDate,
        },
      });

      reset();
      onClose();
    } catch (error) {
      // Handle API validation errors with field-level details
      if (error instanceof ApiError && error.details && error.details.length > 0) {
        error.details.forEach((detail) => {
          setError(detail.field as keyof ReturnToYardFormData, {
            type: 'server',
            message: detail.message,
          });
        });
      }
      // Generic error toast handled by mutation
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleYardSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedYard = yards.find(yard => yard.id === e.target.value);
    if (selectedYard) {
      // Auto-fill the address when a yard is selected
      const addressField = document.getElementById('yardAddress') as HTMLInputElement;
      if (addressField) {
        addressField.value = selectedYard.address;
      }
    }
  };

  if (!container) return null;

  // Only show modal for containers that are not at yard
  const canReturnToYard = container.location.type !== 'yard';

  if (!canReturnToYard) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Return Container to Yard</DialogTitle>
          <DialogDescription>
            Return container {container.containerNumber} to a storage yard. All fields marked with * are required.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Container Info */}
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <h4 className="text-sm font-semibold text-gray-900 mb-2">Container Details</h4>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-gray-600">Number:</span>
                <span className="ml-2 font-medium text-gray-900">{container.containerNumber}</span>
              </div>
              <div>
                <span className="text-gray-600">Size:</span>
                <span className="ml-2 font-medium text-gray-900">{container.size}</span>
              </div>
              <div>
                <span className="text-gray-600">Status:</span>
                <span className="ml-2 font-medium text-gray-900">{container.status}</span>
              </div>
              <div>
                <span className="text-gray-600">Current Location:</span>
                <span className="ml-2 font-medium text-gray-900">{container.location.type.replace('_', ' ')}</span>
              </div>
            </div>
          </div>

          {/* Quick Yard Selector (optional helper) */}
          {activeYards.length > 0 && (
            <div className="space-y-2">
              <label htmlFor="yardSelector" className="block text-sm font-medium text-gray-900">
                Select Storage Yard <span className="text-gray-500 text-xs">(optional quick select)</span>
              </label>
              <select
                id="yardSelector"
                onChange={handleYardSelect}
                className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">Choose a yard...</option>
                {activeYards.map((yard) => (
                  <option key={yard.id} value={yard.id}>
                    {yard.name} {yard.isDefault ? '(Default)' : ''}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500">
                Selecting a yard will auto-fill the address below
              </p>
            </div>
          )}

          {/* Yard Address */}
          <div className="space-y-2">
            <label htmlFor="yardAddress" className="block text-sm font-medium text-gray-900">
              Yard Address <span className="text-red-600">*</span>
            </label>
            <textarea
              {...register('yardAddress')}
              id="yardAddress"
              rows={2}
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="123 Main St, City, State 12345"
            />
            {errors.yardAddress && (
              <p className="text-sm text-red-600">{errors.yardAddress.message}</p>
            )}
          </div>

          {/* Return Date */}
          <div className="space-y-2">
            <label htmlFor="returnDate" className="block text-sm font-medium text-gray-900">
              Return Date & Time <span className="text-red-600">*</span>
            </label>
            <input
              {...register('returnDate')}
              id="returnDate"
              type="datetime-local"
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
            {errors.returnDate && (
              <p className="text-sm text-red-600">{errors.returnDate.message}</p>
            )}
          </div>

          {/* Condition Update */}
          <div className="space-y-2">
            <label htmlFor="condition" className="block text-sm font-medium text-gray-900">
              Update Condition <span className="text-gray-500 text-xs">(optional)</span>
            </label>
            <select
              {...register('condition')}
              id="condition"
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">Keep current condition ({container.condition})</option>
              {containerConditionEnum.options.map((condition) => (
                <option key={condition} value={condition}>
                  {condition.replace('_', ' ').charAt(0).toUpperCase() + condition.slice(1).replace('_', ' ')}
                </option>
              ))}
            </select>
            {errors.condition && (
              <p className="text-sm text-red-600">{errors.condition.message}</p>
            )}
            <p className="text-xs text-gray-500">
              Update the container&apos;s condition based on its current state
            </p>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <label htmlFor="notes" className="block text-sm font-medium text-gray-900">
              Notes <span className="text-gray-500 text-xs">(optional)</span>
            </label>
            <textarea
              {...register('notes')}
              id="notes"
              rows={3}
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Any notes about the return, damage, or condition..."
            />
            {errors.notes && (
              <p className="text-sm text-red-600">{errors.notes.message}</p>
            )}
          </div>

          {/* Footer Actions */}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Returning...' : 'Return to Yard'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
